import { ControlIcons } from 'owa-control-icons';
import setFeedbackMessage from '../actions/setFeedbackMessage';
import setRatingState from '../actions/setRatingState';
import sendFeedbackMessage from '../actions/sendFeedbackMessage';
import strings from 'owa-strings/owa-common-strings';
import {
    AriaProperties,
    AriaRoles,
    generateDomPropertiesForAria,
    generateNewId,
} from 'owa-accessibility';
import { IconButton, PrimaryButton } from 'office-ui-fabric-react/lib/Button';
import { FocusZone, FocusZoneDirection } from 'office-ui-fabric-react/lib/FocusZone';
import { TextField } from 'office-ui-fabric-react/lib/TextField';
import { format } from 'owa-strings';
import { observer } from 'mobx-react';
import {
    setScreenshotImageFromFileList,
    setScreenshotFileState,
} from '../actions/setScreenshotImageActions';
import { store } from '../store/store';
import {
    isBrowserIE,
    isBrowserSafari,
    isBrowserChrome,
    isChromiumOs,
} from 'owa-user-agent/lib/userAgent';
import { isMinimumBrowserVersion } from 'owa-user-agent';
import * as React from 'react';
import FeedbackAreaDropdown from './FeedbackAreaDropdown';

let styles = require('./FeedbackForm.scss');
let classNames = require('classnames');

function isChromeUnder64OnChromiumOS() {
    return isChromiumOs() && isBrowserChrome() && !isMinimumBrowserVersion([64]);
}

// Paste Screen shot unsupported browsers issues:
// 1. All IE::
// VSO:19173 - [feedback tool] investigate why onpaste handler is not called for IE on paste images to textarea
// 2. All Mac Safari:
// VSO:19175 - [feedback tool] investigate why onpaste handler for textareas is not working for image in Mac Safari browser
// 3. Chromium OS under Chrome 64
// It a bug that clipboard will not work for the image paste for screen shot and fixed in Chrome 64
// https://bugs.chromium.org/p/chromium/issues/detail?id=675050
const doesBrowserSupportPastingImageToTextAreas =
    !isBrowserIE() && !isBrowserSafari() && !isChromeUnder64OnChromiumOS();

export interface FeedbackFormProps {
    onSendFeedback?: () => void;
}

@observer
export default class FeedbackForm extends React.Component<FeedbackFormProps, {}> {
    private onInput = (newValue: any) => {
        setFeedbackMessage(store, newValue);
    };

    private onPaste = event => {
        let items: FileList = (event.clipboardData || event.originalEvent.clipboardData).items;
        setScreenshotImageFromFileList(store, items);
    };

    private rate = (rating: number) => {
        setRatingState(store, rating);
    };

    private removeScreenshot = () => {
        setScreenshotFileState(store, null, null);
    };

    private renderScreenshot = (screenshotFileBase64: string) => {
        return (
            <div className={styles.screenshotContainer}>
                <img className={styles.screenshot} src={screenshotFileBase64} />
                <IconButton
                    className={styles.removeScreenshot}
                    onClick={this.removeScreenshot}
                    iconProps={{
                        iconName: ControlIcons.Cancel,
                    }}
                />
            </div>
        );
    };

    private renderRating = (rating: number, describedById: string) => {
        return [1, 2, 3, 4, 5].map((i: number) => {
            let isSelected = i == rating;

            let ratingStyles = classNames(styles.rating, i <= rating ? styles.ratingFill : '');

            let ariaProps: AriaProperties = {
                role: AriaRoles.radio,
                checked: isSelected,
                selected: isSelected,
                label: format(strings.feedbackRating_ariaLabel, i),
                describedBy: describedById,
            };

            return (
                <div key={i} className={styles.ratingBlock}>
                    <IconButton
                        className={ratingStyles}
                        iconProps={{
                            iconName:
                                i <= rating
                                    ? ControlIcons.FavoriteStarFill
                                    : ControlIcons.FavoriteStar,
                        }}
                        onClick={() => this.rate(i)}
                        data-is-focusable="true"
                        {...generateDomPropertiesForAria(ariaProps)}
                    />
                </div>
            );
        });
    };

    render() {
        let { message, rating, screenshotBlob, screenshotFileBase64 } = store;
        let ratingsDomId = generateNewId('FeedbackRating');

        return (
            <div className={styles.menuFeedback}>
                <TextField
                    multiline
                    value={message}
                    onChanged={this.onInput}
                    onPaste={this.onPaste}
                    styles={{ field: styles.textArea }}
                    label={strings.whatDoYouThink}
                    description={
                        doesBrowserSupportPastingImageToTextAreas &&
                        !screenshotBlob &&
                        !screenshotFileBase64
                            ? strings.whatDoYouThinkScreenshot
                            : null
                    }
                />
                {screenshotBlob &&
                    screenshotFileBase64 &&
                    this.renderScreenshot(screenshotFileBase64)}
                <FocusZone
                    direction={FocusZoneDirection.horizontal}
                    className={styles.ratingContainer}
                    rootProps={{
                        role: AriaRoles.radiogroup,
                    }}>
                    <label id={ratingsDomId}>{strings.rateUs}</label>
                    {this.renderRating(rating, ratingsDomId)}
                </FocusZone>
                <FeedbackAreaDropdown />
                <PrimaryButton
                    onClick={this.onSend}
                    className={styles.sendButton}
                    text={strings.sendFeedback}
                />
            </div>
        );
    }

    private onSend = () => {
        sendFeedbackMessage();

        if (this.props.onSendFeedback) {
            this.props.onSendFeedback();
        }
    };
}
